<template>
    <div class="container-fluid">
        <form id="wizardSteps" @submit.prevent="onAddTag()">
            <header class="wizard-header">
                <div class="wizard-header-meta">
                    <span class="wizard-step-count">Step {{ currentStep }} of {{ steps.length }}</span>
                    <span class="wizard-step-name">{{ steps[currentStep - 1] }}</span>
                </div>
                <div class="wizard-track" role="progressbar" :aria-valuenow="currentStep" aria-valuemin="1" :aria-valuemax="steps.length">
                    <span
                        v-for="(name, index) in steps"
                        :key="name"
                        class="wizard-track-seg"
                        :class="{ 'is-done': index < currentStep }"
                    ></span>
                </div>
            </header>

            <div class="tab-content">
            <!-- Event Type Step -->
            <div class="tab-pane fade show active" id="wizardStepOne" role="tabpanel" aria-labelledby="wizardTabOne">
                <!-- Header -->
                <div class="row justify-content-center">
                    <div class="col-12 col-md-10 col-lg-8 col-xl-6 text-center">
                        <!-- Title -->
                        <h1 class="mb-3">                                                                                                                        
                            <!-- Let’s start with template creation. -->
                            Begin by selecting an Event Type to build your Tag Template.
                        </h1>
                        <!-- Subtitle -->
                        <p class="mb-5 text-muted">
                            Event Type selection will incorporate both Paid and Owned media events. This will distinguish the type of data that is collected with your tag. 
                        </p>
                    </div>
                </div> <!-- / .row -->
                <div class="row justify-content-center">
                    <div class="col-12">
                        <Tiles :tiles="eventTypeTiles" @onSelectTile="onSelectEventType" :selectedTile="tagTemplate.event_type"></Tiles>
                    </div>
                </div>            

                <!-- Footer -->
                <div class="nav row align-items-center justify-content-between wizard-footer">
                    <div class="col-auto">
                        <!-- Button -->
                        <button class="btn btn-lg btn-white " type="reset" data-bs-dismiss="modal">Cancel</button>
                    </div>
                    <div class="col-auto">
                        <!-- Button -->
                        <a  v-show="tagTemplate.event_type !== null" class="btn btn-lg btn-primary text-white" data-toggle="wizard" href="#wizardStepTwo">Continue</a>
                    </div>
                </div>
            </div>

            <!-- Platform Step -->
            <div class="tab-pane fade" id="wizardStepTwo" role="tabpanel" aria-labelledby="wizardTabTwo">

                <!-- Header -->
                <div class="row justify-content-center">
                    <div class="col-12 col-md-10 col-lg-8 col-xl-6 text-center">
                        <!-- Title -->
                        <h1 class="mb-3">
                            Select the service you would like to utilize to initiate data (optional)
                        </h1>
                        <!-- Subtitle -->
                        <p class="mb-5 text-muted">
                            Platform selection will  enable data syncing with the relevant DSP or Campaign Manager we support for tag generation.
                        </p>
                    </div>
                </div> <!-- / .row -->
                <div class="row justify-content-center">
                    <div class="col-12">
                        <InitiatorPicker :tiles="initiatorTiles" @onSelectTile="onSelectPlatform" :selectedTile="tagTemplate.initiator"></InitiatorPicker>
                    </div>
                </div>

                <!-- Footer -->
                <div class="nav row align-items-center justify-content-between wizard-footer">
                    <div class="col-auto">
                        <!-- Button -->
                        <a class="btn btn-lg btn-white" data-toggle="wizard" href="#wizardStepOne">Back</a>
                    </div>
                    <div class="col-auto">
                        <!-- Button -->
                        <a class="btn btn-lg" :class="tagTemplate.initiator?'btn-primary text-white':'btn-white'" data-toggle="wizard" href="#wizardStepThree">
                            <span v-if="tagTemplate.initiator">Continue</span>
                            <span v-else >Skip</span>
                        </a>
                    </div>
                </div>

            </div>

            <!-- Tag Type-->
            <div class="tab-pane fade" id="wizardStepThree" role="tabpanel" aria-labelledby="wizardTabThree">

                <!-- Header -->
                <div class="row justify-content-center">
                    <div class="col-12 col-md-10 col-lg-8 col-xl-6 text-center">
                        <!-- Title -->
                        <h1 class="mb-3">                                                                                                                        
                            <!-- Let’s start with template creation. -->
                            Select the Tag Type that will be generated. 
                        </h1>
                        <!-- Subtitle -->
                        <p class="mb-5 text-muted">
                            Tag Type selection will distinguish the type of tag that is generated, relative to your event type. 
                        </p>
                    </div>
                </div> <!-- / .row -->
                <div class="row justify-content-center wizard-question">
                    <div class="col-12 text-center">
                        <!-- Title -->
                        <h3 class="mb-3">
                            Would you like to create an Atomic OR a Container tag?
                        </h3>
                    </div>
                    <div class="col-12">
                        <Tiles :tiles="tagTypeTiles" @onSelectTile="onSelectTagType" :selectedTile="tagType"></Tiles>
                    </div>
                </div>

                <div v-show="tagType=='atomic'">
                    <div class="row justify-content-center wizard-question">
                        <div class="col-12 text-center">
                            <!-- Title -->
                            <h3 class="mb-3">
                                Would you like to generate image or script tag?
                            </h3>
                        </div>
                        <div class="col-12">
                            <Tiles :tiles="atomicTagTypeTiles" @onSelectTile="onSelectAtomicTagType" :selectedTile="atomicTagType"></Tiles>
                        </div>
                    </div>
                    <div v-show="atomicTagType=='img'" class="row justify-content-center wizard-question">
                        <div class="col-12 text-center">
                            <!-- Title -->
                            <h3 class="mb-3">
                                You've chosen an Image Atomic Tag Type, what format would you like to generate?
                            </h3>
                        </div>
                        <div class="col-12">
                            <Tiles :tiles="atomicImageTagFormatTiles" @onSelectTile="onSelectTagFormat" :selectedTile="tagTemplate.tag_type"></Tiles>
                        </div>
                    </div>
                    <div v-show="atomicTagType=='js'" class="row justify-content-center wizard-question">
                        <div class="col-12 text-center">
                            <!-- Title -->
                            <h3 class="mb-3">
                                You've chosen an Script Atomic Tag Type, what format would you like to generate?
                            </h3>
                        </div>
                        <div class="col-12">
                            <Tiles :tiles="atomicScriptTagFormatTiles" @onSelectTile="onSelectTagFormat" :selectedTile="tagTemplate.tag_type"></Tiles>
                        </div>
                    </div>

                </div>

                <div v-show="tagType=='container'" class="row justify-content-center wizard-question">
                    <div class="col-12 text-center">
                        <!-- Title -->
                        <h3 class="mb-3">
                            You've chosen an Container Tag Type, what format would you like to generate?
                        </h3>
                    </div>
                    <div class="col-12">
                        <Tiles :tiles="containerTagFormatTiles" @onSelectTile="onSelectTagFormat" :selectedTile="tagTemplate.tag_type"></Tiles>
                    </div>
                </div>
                <!-- Footer -->
                <div class="nav row align-items-center justify-content-between wizard-footer">
                    <div class="col-auto">                  
                        <!-- Button -->
                        <a class="btn btn-lg btn-white" data-toggle="wizard" href="#wizardStepTwo">Back</a>
                    </div>
                    <div class="col-auto">
                        <!-- Button -->
                        <a v-show="tagTemplate.tag_type" class="btn btn-lg btn-primary text-white" data-toggle="wizard" href="#wizardStepFour">Continue</a>
                    </div>
                </div>
            </div>

            <!-- Logging Mode -->
            <div class="tab-pane fade" id="wizardStepFour" role="tabpanel" aria-labelledby="wizardTabFour">
                <!-- Header -->
                <div class="row justify-content-center">
                    <div class="col-12 col-md-10 col-lg-8 col-xl-6 text-center">
                    <!-- Title -->
                    <h1 class="mb-3">
                        Would you like  to enable logging Mode?
                    </h1>                 

                    </div>
                </div> <!-- / .row -->
                <div class="row justify-content-center text-center wizard-question">
                    <div class="col-12">
                        <Tiles :tiles="loginModeTiles" @onSelectTile="onLogginModeEnable" :selectedTile="enableLogginMode"></Tiles>
                    </div>
                </div>
                <div v-if="enableLogginMode">
                    <div class="row justify-content-center wizard-question">
                        <div class="col-12 col-lg-10 text-center">
                            <!-- Title -->
                            <h3 class="mb-3">
                                Choose a Log Delivery
                            </h3>
                            <Tiles :tiles="logDeliveryStreaming?loginDeliveryTilesWithStreaming:loginDeliveryTiles" @onSelectTile="onSelectLogginDelivery" :selectedTile="logDelivery"></Tiles>
                        </div>
                    </div>
                    <div class="row justify-content-center wizard-question">
                        <div class="col-12 col-lg-10 text-center">
                            <!-- Title -->
                            <h3 class="mb-3">
                                Choose a Log Size
                            </h3>
                            <Tiles :tiles="loginSizeTiles" @onSelectTile="onSelectLogginSize" :selectedTile="logSize"></Tiles>
                        </div>
                    </div>
                    <div class="row justify-content-center wizard-question">
                        <div class="col-12 col-lg-10 text-center">
                            <!-- Title -->
                            <h3 class="mb-3">
                                Choose a Log IP Type
                            </h3>
                            <Tiles :tiles="loginIPTypeTiles" @onSelectTile="onSelectLogginIPType" :selectedTile="logIPType"></Tiles>
                        </div>
                    </div>
                </div>
                <!-- Footer -->
                <div class="nav row align-items-center justify-content-between wizard-footer">
                    <div class="col-auto">
                        <!-- Button -->
                        <a class="btn btn-lg btn-white" data-toggle="wizard" href="#wizardStepThree">Back</a>
                    </div>
                    <div class="col-auto">
                        <!-- Button -->
                        <a class="btn btn-lg btn-primary text-white" data-toggle="wizard" href="#wizardStepFive">Continue</a>
                    </div>
                </div>

            </div>

            <!-- Tag Name -->
            <div class="tab-pane fade" id="wizardStepFive" role="tabpanel" aria-labelledby="wizardTabFive">
                <!-- Header -->
                <div class="row justify-content-center">
                    <div class="col-12 col-md-10 col-lg-8 col-xl-6 text-center">
                    <!-- Title -->
                    <h1 class="mb-3">
                        Name your Tag Template
                    </h1>
                    </div>
                </div> <!-- / .row -->
                
                <div class="row justify-content-center">
                    <div class="col-12 col-md-8 col-lg-6">
                        <div class="mb-3">
                            <h3 for="exampleFormControlInput1" class="form-label">Name</h3>
                            <input  class="form-control" id="exampleFormControlInput1" v-model="tagTemplate.name">
                        </div>
                        <div class="mb-3">
                            <h3 for="exampleFormControlInput1" class="form-label">Category Tags</h3>
                            <InputField
                                v-model="tagTemplate.tag"
                                :options="tagTemplate.tag"
                                :showLabel="false"
                                type="multiselect"
                            />
                        </div>
                        <div v-if="this.$store.getters.userRoles.isAqferAdmin || this.$store.getters.userRoles.isAqferManager" class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="tagTemplate.public" id="flexCheckDefault">
                            <h3 class="form-check-label" for="flexCheckDefault">
                                Would you like to make this tag template public?  
                            </h3>
                        </div>
                    </div>
                </div>  
                <!-- Footer -->
                <div class="nav row align-items-center justify-content-between wizard-footer">
                    <div class="col-auto">
                        <!-- Button -->
                        <a class="btn btn-lg btn-white" data-toggle="wizard" href="#wizardStepFour">Back</a>
                    </div>
                    <div class="col-auto" :class="{ invisible: !tagTemplate.name }">
                        <!-- Button -->
                        <button class="btn btn-lg btn-primary" type="submit">
                            <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else>
                                Create
                            </div>       
                        </button>                         
                    </div>
                </div>
            </div>
            </div>
        </form>
    </div>

</template>

<script>
import { Tab } from 'bootstrap';
import { mapActions } from 'vuex'
import  Tiles  from '../Tiles.vue';
import InitiatorPicker from '../aut/InitiatorPicker.vue';
import { isDuplicate } from '../../utils/commonFunction';
import { tag_event_types } from '../../utils/constOptionValues';
import InputField from "../InputField.vue";
const logKeys = {
                'b':'bbh',
                'c':'bbi',
                'd':'beh',
                'e':'bei',
                'f':'sbh',
                'g':'sbi',
                'h':'seh',
                'i':'sei',
                'j':'bbn',
                'k':'ben',
                'l':'sbn',
                'm':'sen',
            }

export default {
    components: {Tiles, InitiatorPicker, InputField},
    data() {
        return {
            steps: ["Event Type", "Initiator", "Tag Type", "Logging", "Name"],
            currentStep: 1,
            tagTemplate: {
                owner: this.$store.state.currentUser.email,
                cid: this.$store.state.currentClient.cid,
                name: null,
                event_type: null,
                initiator: null,
                tag_type: null,
                cookie: null,
                public: false,
                logging_mode: null
            }, 

            initiatorTiles: [],
                    
            
            atomicTagTypeTiles: [
                    {title:"Javascript", subTitle:"Allows for extra collection of data on the page.", value:"js"},
                    {title:"Image", subTitle:"Raw logging pixel with query parameters.", value: "img"},
                    {title:"Redirect", subTitle:"URL to collect and then redirect to a certain URL.", value: "r"},
                ],
            
            atomicTagType: null,            
            tagTypeTiles: [
                    {title:"Atomic", subTitle:"A pixel or endpoint with the purpose of firing once.", value:"atomic"},
                    {title:"Container", subTitle:"Allows the ability to fire additional tags.", value: "container"},
                ],           
            tagType: null,            

            loginModeTiles: [
                {title:"Yes", subTitle:"Store logs for development or error checking purposes", value:true},
                {title:"No", subTitle:"Do not store additional logs.", value: false},
            ],
            loginDeliveryTiles: [
                { title: "Batch", value: "b" }
            ],
            loginDeliveryTilesWithStreaming: [
                { title: "Batch", value: "b" },
                { title: "Streaming", value: "s" }
            ],
            loginSizeTiles: [
                { title: "Base", value: "b" },
                { title: "Extended", value: "e" },
            ],
            loginIPTypeTiles: [
                { title: "Hashed IP", value: "h" },
                { title: "Plain IP", value: "i" },
                { title: "No IP", value: "n" },
            ],                  
            atomicImageTagFormatTiles: [
                { title:"Cookie  (Recommended)", subTitle:"Sets unique cookie if it is not present.", value:"a.gif"},
                { title:"NoCookie", subTitle:"No additional logic.", value: "c.gif"},
            ],           
            atomicTagFormat: null,
            atomicScriptTagFormatTiles: [
                { title:"Cookie  (Recommended)", subTitle:"Sets unique cookie if it is not present.", value:"c.js"},
                { title:"Statistical", subTitle:"Additional Statistical ID logic to determine unique users.", value: "s.js"},
                { title:"Both", subTitle:"Cookie and Statistical ID logic combined.", value: "cs.js"},
            ],
            containerTagFormatTiles: [
                { title:"Cookie", subTitle:"Sets unique cookie if it is not present.", value:"tc.dhj"},
                { title:"Statistical", subTitle:"Additional Statistical ID logic to determine unique users.", value: "ts.dhj"},
                { title:"Both", subTitle:"Cookie and Statistical ID logic combined.", value: "tcs.dhj"},
                { title:"None", subTitle:"No additional logic.", value: "t.dhj"},
            ],
            loading: false,
            logDelivery: null,
            logSize: "",
            logIPType: "",
            editMode: true,
            enableLogginMode:null,
            tag: null
        }
    },

    computed: {
        //Note: Tiles are derived from the shared event type list so the wizard stays in sync with it
        eventTypeTiles() {
            let tiles = tag_event_types.map((eventType) => {
                return { title: eventType.label, subTitle: eventType.description, value: eventType.value }
            });
            return tiles;
        },
        loggingMode() {
            let logMode = this.logDelivery+this.logSize+this.logIPType;
            for (const key in logKeys) {
                if(logKeys[key] == logMode)
                    return key
            }
            return 'a';
        },
        logDeliveryStreaming() {
            return (this.$store.state.aut.autClientData.config && this.$store.state.aut.autClientData.config.logDeliveryStreaming)?this.$store.state.aut.autClientData.config.logDeliveryStreaming: false;
        }      
    },

    watch: {        
        loggingMode(value, oldValue) {
            this.tagTemplate.logging_mode = value;
        }
    },

    mounted() {
        const stepIds = ['#wizardStepOne', '#wizardStepTwo', '#wizardStepThree', '#wizardStepFour', '#wizardStepFive'];
        const toggles = document.querySelectorAll('[data-toggle="wizard"]');
        toggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                // Toggle new tab
                const tab = new Tab(toggle);
                tab.show();
                // Remove active state
                toggle.classList.remove('active');
                const target = stepIds.indexOf(toggle.getAttribute('href'));
                if (target !== -1) this.currentStep = target + 1;
            });
        });
    },
    beforeMount(){
        let self=this;
        this.$store.dispatch('aut/fetchAutClientData', this.$store.state.currentClient.cid);
        this.$store.dispatch("aut/fetchInitiators").then(()=>{
            self.initiatorTiles= self.$store.state.aut.initiators.map((initiator)=>{
                return { title:initiator.name, 
                            subTitle:initiator.description,
                            value:initiator.value, 
                            iconSrc:initiator.logo
                        } 
            })
        })
        },
    methods: {
        ...mapActions('aut', {
            createTagTemplate: 'createTagTemplate',
            fetchTagTemplates: 'fetchTagTemplates'
        }),
        async onAddTag() {
            this.loading = true;
            let toastoptions;
            let response;
            let valid = false;
            try {
                valid = !isDuplicate(this.tagTemplate.name,this.$store.state.aut.tagTemplates, "name", null)
                if(valid){
                    this.tagTemplate.tag= this.tagTemplate.tag ?this.tagTemplate.tag.toString():"";
                    response = await this.createTagTemplate(this.tagTemplate)
                    toastoptions = {
                        message: "Tag added successfully.",
                        type: "success"
                    }
                }else{
                    toastoptions = {
                        message: "Already '"+this.tagTemplate.name+"' name exits.'",
                        type: "error",
                    };
                }
            } catch (error) {                
                // if (error.response.data.error) 
                //     this.error = error.response.data.error
                // else 
                //     this.error = error.message

                toastoptions = {
                    message: "Something went wrong when adding the tag. Please contact support.",
                    type: "error"
                }
                return
            }
            //Dont hide and redirect page when name is duplicate
            if (valid){
                this.$store.dispatch('hideModal')
                if(response.data.id)
                    this.$router.push({ name: 'Tag Template', params: {id: response.data.id}} );
                else
                    this.$store.dispatch('aut/fetchTagTemplates')
            }
            this.loading = false;
            this.$store.dispatch('addToast', toastoptions);
        },
        
        onSelectEventType(val) {
            this.tagTemplate.event_type = val;
            if(val == "clk") {
                this.onSelectTagType("atomic");
                this.onSelectAtomicTagType("r");
            }
        },
        onSelectPlatform(val) {
            this.tagTemplate.initiator = val;
        },
        onSelectTagType(val) {
            this.tagType = val;
        },
        onSelectTagFormat(val) {
            this.tagTemplate.tag_type = val;
        },
        
        onSelectAtomicTagType(val) {
           this.atomicTagType = val;
           if(this.atomicTagType == "r") {
                this.tagTemplate.tag_type = "r";
           }
        },
        onSelectAtomicTagFormat(val) {
            this.atomicTagFormat = val;
        },       
        onLogginModeEnable(val) {
            this.enableLogginMode = val;
            if(!this.enableLogginMode){
                this.tagTemplate.logging_mode = "a";
                this.logDelivery = null;
                this.logSize = null;
                this.logIPType = null;
            }
            else{
                this.logDelivery = "b";
                this.logSize = "e";
                this.logIPType = "h";
            }
            
        },
        onSelectLogginDelivery(val) {
            this.logDelivery = val;

        },
        onSelectLogginSize(val) {
            this.logSize = val;

        },
        onSelectLogginIPType(val) {
            this.logIPType = val;

        }
    }

}
</script>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

#wizardSteps {
    .wizard-header {
        position: sticky;
        top: 0;
        z-index: 4;
        padding: 1.5rem 0 1rem;
        background-color: #fff;
    }

    .wizard-header-meta {
        display: flex;
        align-items: baseline;
        gap: 0.625rem;
        margin-bottom: 0.625rem;
    }

    .wizard-step-count {
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: $gray-600;
    }

    .wizard-step-name {
        font-size: 0.9375rem;
        font-weight: 600;
        color: $gray-900;
    }

    .wizard-track {
        display: flex;
        gap: 0.25rem;
    }

    .wizard-track-seg {
        flex: 1 1 0;
        height: 3px;
        border-radius: 2px;
        background-color: $border-color;
        transition: background-color 0.25s ease;

        &.is-done {
            background-color: #2C7BE5;
        }
    }

    .tab-pane {
        min-height: 42vh;
        padding-top: 1.5rem;
    }

    .tab-pane.active {
        display: flex;
        flex-direction: column;
    }

    h1 {
        font-size: 1.375rem;
        line-height: 1.35;
        margin-bottom: 0.5rem !important;
    }

    .tab-pane > .row:first-child p.text-muted {
        font-size: 0.875rem;
        line-height: 1.5;
        margin-bottom: 1.75rem !important;
        margin-left: auto;
        margin-right: auto;
    }

    .form-label {
        font-size: 0.8125rem;
        font-weight: 600;
        margin-bottom: 0.375rem;
    }

    .form-check-label {
        font-size: 0.875rem;
        font-weight: 400;
        margin-bottom: 0;
    }

    /* Tiles.vue sizes .tile-col for a full-width modal. This one is capped well
       below that, so the tiles are narrowed to keep four per row instead of
       three - step 1 offers a dozen event types and would otherwise run long.
       Overridden here rather than in Tiles.vue, which other wizards share. */
    .tile-col {
        width: 13rem;
    }

    .wizard-question {
        margin-bottom: 1rem;

        /* Tiles.vue hard-codes mb-5 on its own row, sized for a full page
           rather than a stack of questions inside a modal */
        .row.mb-5 {
            margin-bottom: 1.25rem !important;
        }

        h3 {
            font-size: 1.0625rem;
            margin-bottom: 0.75rem !important;
        }

        /* Note: no margin override on .card-tile - Tiles.vue now spaces tiles
           with row gutters, and a margin would inset the card and break the
           equal-height (h-100) alignment between tiles in a row. */
        .card-tile .card-body {
            padding: 0.75rem 0.875rem;
        }

        .card-tile .card-title {
            font-size: 0.9375rem;
            margin-bottom: 0.25rem;
        }

        .card-tile .card-subtitle {
            font-size: 0.75rem;
            line-height: 1.35;
        }
    }

    .wizard-footer {
        position: sticky;
        bottom: 0;
        z-index: 3;
        margin-top: auto;
        padding: 1.5rem 0 1rem;
        background-color: #fff;
        border-top: 1px solid $border-color;
    }
}

body.dark-mode #wizardSteps {
    .wizard-footer {
        background-color: $dark-card-bg;
        border-top-color: $dark-border-color;
    }

    .wizard-header {
        background-color: $dark-card-bg;
    }

    .wizard-step-name {
        color: $dark-text-primary;
    }

    .wizard-track-seg {
        background-color: $dark-border-color;

        &.is-done {
            background-color: #7ab0ff;
        }
    }
}
</style>